import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Sparkles, Loader2, Tag, CheckCircle2, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { TillDeliveryTeamCard } from './TillDeliveryTeamCard';

const PLAN_COPY = {
  monthly: { title: 'Monthly', sub: '30 days of weekly plans' },
};

const PlanCard = ({ id, item, selected, onSelect }) => {
  const copy = PLAN_COPY[id] || { title: id, sub: '' };
  const price = item.final_amount ?? item.price_amount;
  const fmt = (n) => `${item.razorpay_currency === 'USD' ? '$' : '₹'}${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  return (
    <button type="button" onClick={() => onSelect(id)} data-testid={`paywall-plan-${id}`}
      className={`relative text-left rounded-2xl border p-4 transition-all duration-200 w-full
        ${selected ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/30' : 'border-border hover:border-primary/40'}`}>
      {item.badge && <Badge className="absolute -top-2.5 right-3 bg-amber-100 text-amber-900 border-amber-200 text-[10px]">{item.badge}</Badge>}
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-heading font-semibold text-sm">{copy.title}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{copy.sub}</p>
        </div>
        <div className="text-right shrink-0">
          {item.mrp_amount && item.mrp_amount > price && (
            <p className="text-[11px] text-muted-foreground line-through">{fmt(item.mrp_amount)}</p>
          )}
          <p className="font-heading text-lg font-semibold" data-testid={`paywall-price-${id}`}>{fmt(price)}</p>
          {item.promo_applied && <p className="text-[10px] text-emerald-700">coupon −{fmt(item.discount_amount)}</p>}
          {item.promo_error && <p className="text-[10px] text-amber-700" data-testid={`paywall-promo-error-${id}`}>coupon n/a · {promoReasonText(item.promo_error.reason)}</p>}
        </div>
      </div>
    </button>
  );
};

const TaxBreakup = ({ item }) => {
  if (!item?.tax) return null;
  const t = item.tax;
  const sym = item.razorpay_currency === 'USD' ? '$' : '₹';
  const f = (n) => `${sym}${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  return (
    <div className="rounded-xl bg-muted/50 p-3 text-xs space-y-1" data-testid="paywall-tax-breakup">
      {item.promo_applied && (
        <div className="flex justify-between text-emerald-700"><span>Coupon {item.promo_applied.code}</span><span>−{f(item.discount_amount)}</span></div>
      )}
      <div className="flex justify-between"><span className="text-muted-foreground">Base amount</span><span>{f(t.taxable_amount)}</span></div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">GST {t.gst_pct ? `(${t.gst_pct}%)` : ''}</span>
        <span>{t.international ? 'Nil (international)' : f(t.gst_amount)}</span>
      </div>
      <div className="flex justify-between font-semibold border-t pt-1.5 mt-1"><span>You pay</span><span data-testid="paywall-total">{f(t.total)}</span></div>
    </div>
  );
};

// OPS V76 — brute-force guard reason gets a friendly line
const promoReasonText = (reason) => (reason === 'TOO_MANY_ATTEMPTS'
  ? 'Too many attempts, try again in a few minutes.'
  : String(reason || '').replace(/_/g, ' ').toLowerCase());

export const CoachPaywallDialog = ({ open, onClose, onPurchased, reason }) => {
  const { user } = useAuth();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState('monthly');   // V31 — monthly is the only self-serve plan
  const [enquiry, setEnquiry] = useState(null);
  const [coupon, setCoupon] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [paying, setPaying] = useState(false);

  const fetchQuote = useCallback(async (code) => {
    setLoading(true);
    try {
      try {
        const st = (await api.get('/api/learner/me')).data?.state;
        setEnquiry(st?.sales_enquiries?.soulai_till_delivery || null);
      } catch { /* fall back to server resolution */ }
      const r = await api.get('/api/pricing/catalog', { params: { placement: 'soulai_coach', ...(code ? { promo_code: code } : {}) } });
      setQuote(r.data);
      if (code) {
        if (r.data.promo_applied?.valid) { setAppliedCode(code); toast.success(`Coupon ${code.toUpperCase()} applied`); }
        else { setAppliedCode(''); toast.error(r.data.promo_applied?.reason === 'TOO_MANY_ATTEMPTS' ? promoReasonText('TOO_MANY_ATTEMPTS') : `Coupon not valid (${promoReasonText(r.data.promo_applied?.reason || 'INVALID')})`); }
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not load plans'); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) { setAppliedCode(''); setCoupon(''); fetchQuote(''); } }, [open, fetchQuote]);

  const item = quote?.items?.[selected];

  const pay = async () => {
    if (!quote || !item) return;
    setPaying(true);
    try {
      const orderRes = await api.post('/api/payment/create-order', { quote_id: quote.quote_id, method: 'razorpay', item_key: selected });
      const { order_id, razorpay_key_id } = orderRes.data;
      if (!order_id || !razorpay_key_id || !window.Razorpay) { toast.error('Payment gateway unavailable. Please try again.'); setPaying(false); return; }
      const rzp = new window.Razorpay({
        key: razorpay_key_id, amount: item.razorpay_amount_paise, currency: item.razorpay_currency, order_id,
        name: 'Soul Sanskar Academy', description: `SoulAI Coach — ${PLAN_COPY[selected]?.title || selected}`,
        prefill: { name: user?.name || '', email: user?.email || '', contact: user?.phone || '' },
        theme: { color: '#4A6741' },
        modal: { ondismiss: () => setPaying(false) },
        handler: async (resp) => {
          try {
            const v = await api.post('/api/payment/verify', { order_id, payment_id: resp.razorpay_payment_id, signature: resp.razorpay_signature });
            if (v.data.verified) { toast.success('Welcome aboard! Your SoulAI Coach is active.'); onPurchased?.(); onClose?.(); }
            else toast.error('Payment verification failed. Contact support.');
          } catch { toast.error('Verification error. Contact support if charged.'); }
          setPaying(false);
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start payment');
      setPaying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="coach-paywall-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />Unlock your full SoulAI plan</DialogTitle>
          <DialogDescription>
            {reason || 'Your free class is done. Continue with weekly, safety-matched classes for the rest of your pregnancy.'}
          </DialogDescription>
        </DialogHeader>
        {loading && !quote ? (
          <div className="space-y-3"><Skeleton className="h-20 rounded-2xl" /><Skeleton className="h-20 rounded-2xl" /></div>
        ) : quote ? (
          <div className="space-y-3">
            <div className="grid gap-2.5">
              {quote.items.till_delivery && (
                <TillDeliveryTeamCard item={quote.items.till_delivery} enquiry={enquiry} onEnquiry={setEnquiry} />
              )}
              <PlanCard id="monthly" item={quote.items.monthly} selected={selected === 'monthly'} onSelect={setSelected} />
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Tag className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" className="pl-9 h-9 text-sm uppercase" data-testid="paywall-coupon-input" disabled={!!appliedCode} />
              </div>
              {appliedCode ? (
                <Button variant="outline" size="sm" className="h-9" onClick={() => { setCoupon(''); fetchQuote(''); setAppliedCode(''); }} data-testid="paywall-coupon-remove"><X className="h-3.5 w-3.5 mr-1" />Remove</Button>
              ) : (
                <Button variant="outline" size="sm" className="h-9" disabled={!coupon.trim() || loading} onClick={() => fetchQuote(coupon.trim())} data-testid="paywall-coupon-apply">
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
                </Button>
              )}
            </div>
            {appliedCode && <p className="text-[11px] text-emerald-700 flex items-center gap-1" data-testid="paywall-coupon-applied"><CheckCircle2 className="h-3 w-3" />{appliedCode} applied</p>}
            <TaxBreakup item={item} />
            <Button className="w-full" onClick={pay} disabled={paying || !item} data-testid="paywall-pay-btn">
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay ${item?.tax ? (item.razorpay_currency === 'USD' ? '$' : '₹') + Number(item.tax.total).toLocaleString('en-IN') : ''} securely`}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center flex items-center justify-center gap-1"><ShieldCheck className="h-3 w-3" />Prices include GST where applicable · Cancel anytime by not renewing</p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
